import { useEffect, useState } from "react";
import { getMeta, getPartnerDetail, getPartners } from "../lib/api";
import { EvidencePanel } from "../components/EvidencePanel";
import { ResultNumbers } from "../components/ResultNumbers";
import { DataTable } from "../components/DataTable";
import { Drawer, EvidenceButton } from "../components/Drawer";
import { CaveatNote, EvidenceSection, PlaceChip, StatTile, SummaryBanner, rateTone } from "../components/Evidence";
import { ErrorBox, Loading, PageHeader } from "../components/Shell";
import { inr, num, pct } from "../lib/format";

export default function PartnersPage() {
  const [meta, setMeta] = useState(null);
  const [fy, setFy] = useState("");
  const [theme, setTheme] = useState("");
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [evidence, setEvidence] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    getMeta().then(setMeta).catch(setErr);
  }, []);

  useEffect(() => {
    setData(null);
    getPartners({ fy: fy || undefined, theme: theme || undefined }).then(setData).catch(setErr);
  }, [fy, theme]);

  const openPartner = (row) => {
    setDetail(null);
    getPartnerDetail(row.partner, { fy: fy || undefined }).then(setDetail).catch(setErr);
  };

  return (
    <div>
      <PageHeader
        title="Partner scorecard"
        testId="partners-header"
        actions={data && <EvidenceButton onClick={() => setEvidence(true)}
                                         answerability={data.answerability} />}
      />

      <div className="flex gap-2 flex-wrap mb-6" data-testid="partner-filters">
        <Select value={fy} onChange={setFy} testId="filter-fy" label="All financial years"
                options={(meta?.fys || []).map((f) => ({ value: f, label: f }))} />
        <Select value={theme} onChange={setTheme} testId="filter-theme" label="All themes"
                options={(meta?.themes || []).map((t) => ({ value: t.key, label: t.label }))} />
      </div>

      {err && <ErrorBox error={err} />}
      {!data && !err && <Loading />}

      {data && <ResultNumbers result={data} onPartnerClick={openPartner} />}

      <Drawer open={evidence} onClose={() => setEvidence(false)} testId="partners-evidence-drawer">
        {data && (
          <EvidencePanel result={data} testId="partners-evidence">
            <div className="space-y-6">
              <SummaryBanner>
                {num(data.grant_count)} grants across {data.partner_total ?? data.rows.length}{" "}
                canonical partners in this slice. {data.registry_matched_partners} of{" "}
                {data.partner_total ?? data.rows.length} are matched in the
                NGO registry for due diligence.
              </SummaryBanner>
              <CaveatNote title="What is measured">
                Utilisation, unspent balances and year-on-year consistency come from populated
                mGrant financial fields. Milestone adherence and disbursement lag are withheld
                because those fields are empty — the record counts are listed below.
              </CaveatNote>
              <CaveatNote title="How partners are identified">
                Partner names are clustered from mGrant NGO records and implementing-agency
                strings, so one canonical partner can cover several spellings. Registry matching
                uses the CSR registration number first, then an exact legal name — never an
                approximate match, because that produced false pairs in testing.
              </CaveatNote>
            </div>
          </EvidencePanel>
        )}
      </Drawer>

      <Drawer open={!!detail} onClose={() => setDetail(null)} testId="partner-detail-drawer">
        {detail && (
          <div className="h-full overflow-auto p-7" data-testid="partner-detail">
            <div className="text-[12px] font-medium text-[#A01236]">
              Partner ledger
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-2">
              {detail.rows[0].partner}
            </h2>
            <p className="text-[13px] text-stone-600 mt-1">
              {detail.rows[0].csr_no || "no CSR registration number in mGrant"}
              {detail.rows[0].name_variants.length > 1 &&
                ` · ${detail.rows[0].name_variants.length} name variants clustered`}
            </p>

            <div className="grid grid-cols-2 gap-2.5 mt-7">
              <StatTile label="Committed" tone="brand" value={inr(detail.rows[0].committed)} />
              <StatTile label="Utilisation" tone={rateTone(detail.rows[0].closed_utilisation_rate)}
                        value={detail.rows[0].closed_utilisation_rate === null ? "in flight" : pct(detail.rows[0].closed_utilisation_rate)}
                        sub="of funds received on ended grants" />
              <StatTile label="Unutilised" tone={detail.rows[0].closed_funds_unspent > 0 ? "amber" : "neutral"}
                        value={inr(detail.rows[0].closed_funds_unspent)}
                        sub={`${detail.rows[0].closed_grants} ended grants`} />
              <StatTile label="Tranches released" tone="sky"
                        value={detail.rows[0].tranche_count
                          ? `${pct(detail.rows[0].tranche_release_rate)}`
                          : "—"}
                        sub={detail.rows[0].tranche_count
                          ? `of ${detail.rows[0].tranche_count} planned tranches`
                          : "no tranche plan recorded"} />
              <StatTile label="₹ per beneficiary" tone="neutral"
                        value={inr(detail.rows[0].cost_per_beneficiary_internal)}
                        sub={`${num(detail.rows[0].beneficiaries_reached)} declared as reached · mGrant-internal only`} />
              <StatTile label="Consistency"
                        tone={detail.rows[0].consistency_label === "consistent" ? "emerald"
                          : detail.rows[0].consistency_label === "erratic" ? "rose"
                            : detail.rows[0].consistency_label === "variable" ? "amber" : "neutral"}
                        value={detail.rows[0].consistency_label || "under 3 FY"}
                        sub={detail.rows[0].yoy_consistency_cv != null
                          ? `coefficient of variation ${detail.rows[0].yoy_consistency_cv}`
                          : "needs three closed years"} />
            </div>

            <div className="rule my-7" />
            <EvidenceSection
              label="Due diligence · mGrant (first-party)"
              note={detail.rows[0].dd_id || detail.rows[0].dd_status_label}
              testId="partner-dd"
            >
              <div className="flex flex-wrap gap-1.5">
                <PlaceChip>{detail.rows[0].dd_status_label}</PlaceChip>
                {detail.rows[0].mgrant_ngo_status && (
                  <PlaceChip>{detail.rows[0].mgrant_ngo_status}</PlaceChip>
                )}
                {detail.rows[0].mgrant_fcra_status && (
                  <PlaceChip>{detail.rows[0].mgrant_fcra_status}</PlaceChip>
                )}
                {detail.rows[0].mgrant_legal_structure && (
                  <PlaceChip>{detail.rows[0].mgrant_legal_structure}</PlaceChip>
                )}
                {detail.rows[0].mgrant_year_of_establishment && (
                  <PlaceChip>est. {detail.rows[0].mgrant_year_of_establishment}</PlaceChip>
                )}
              </div>

              {detail.rows[0].dd_gap_flag ? (
                <CaveatNote title="No DD record in mGrant">
                  {detail.rows[0].dd_missing_caveat}
                </CaveatNote>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2.5">
                    <StatTile label="Regulatory watchlist"
                              value={detail.rows[0].dd_regulatory_watchlist || "—"}
                              sub={detail.rows[0].dd_regulatory_watchlist_verbatim}
                              title={detail.rows[0].dd_regulatory_watchlist_verbatim} />
                    <StatTile label="Adverse media / blacklist check"
                              value={detail.rows[0].dd_blacklist_verification || "—"}
                              sub={detail.rows[0].dd_blacklist_verification_verbatim}
                              title={detail.rows[0].dd_blacklist_verification_verbatim} />
                  </div>
                  {[
                    ["Financial health, as assessed", detail.rows[0].dd_financial_health_verbatim],
                    ["Site visit", detail.rows[0].dd_site_visit_verbatim],
                    ["What the visit found", detail.rows[0].dd_visit_impressions_verbatim],
                    ["Community interaction", detail.rows[0].dd_community_interaction_verbatim],
                    ["Areas of concern", detail.rows[0].dd_areas_of_concern_verbatim],
                  ].filter(([, v]) => v).map(([label, v]) => (
                    <div key={label}>
                      <div className="text-[12px] text-stone-600 mb-1">
                        {label}
                      </div>
                      <p className="text-[13px] text-stone-700 leading-relaxed border-l-2 border-stone-200 pl-2.5">
                        {v}
                      </p>
                    </div>
                  ))}
                  <CaveatNote title="How to read these entries">
                    {detail.rows[0].dd_assessment_caveat}
                  </CaveatNote>
                </>
              )}
            </EvidenceSection>

            <div className="rule my-7" />
            <EvidenceSection
              label="Own published accounts · give.do (cross-check)"
              note={detail.rows[0].registry_matched
                ? `matched on ${detail.rows[0].registry_match_method?.split("_").join(" ")}`
                : "not in registry"}
              testId="partner-diligence"
            >
              {detail.rows[0].registry_matched ? (
                <>
                  <div className="flex flex-wrap gap-1.5">
                    {(detail.rows[0].registration_types || []).map((t) => (
                      <PlaceChip key={t}>{t.replace("_", " ")}</PlaceChip>
                    ))}
                    {detail.rows[0].pan && <PlaceChip>PAN {detail.rows[0].pan}</PlaceChip>}
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    <StatTile label="Programme expense ratio"
                              value={detail.rows[0].programme_expense_ratio_pct != null
                                ? pct(detail.rows[0].programme_expense_ratio_pct)
                                : "—"}
                              sub={detail.rows[0].own_reporting_year
                                ? `their own ${detail.rows[0].own_reporting_year} accounts`
                                : "no accounts in the snapshot"} />
                    <StatTile label="Our weight in their budget"
                              value={detail.rows[0].funder_dependency_pct != null
                                ? pct(detail.rows[0].funder_dependency_pct)
                                : "—"}
                              sub={detail.rows[0].biggest_bajaj_fy
                                ? `${inr(detail.rows[0].biggest_bajaj_fy_committed_inr)} in ${detail.rows[0].biggest_bajaj_fy} vs ${inr(detail.rows[0].own_total_expenses_inr)} of their spend`
                                : "no comparable year"} />
                  </div>
                  {(detail.rows[0].diligence_reasons || []).length > 0 && (
                    <ul className="space-y-1.5">
                      {detail.rows[0].diligence_reasons.map((x, i) => (
                        <li key={i} className="text-[13px] text-stone-700 flex gap-2 leading-relaxed">
                          <span className="text-[#A01236] mt-0.5">▸</span>
                          <span>{x}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {detail.rows[0].states_beyond_our_grants?.length > 0 && (
                    <div>
                      <div className="text-[12px] text-stone-600 mb-1.5">
                        Reports operating in these states where we do not fund them:
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {detail.rows[0].states_beyond_our_grants.map((s) => (
                          <PlaceChip key={s}>{s}</PlaceChip>
                        ))}
                      </div>
                    </div>
                  )}
                  <CaveatNote title="Source of these figures">
                    {detail.rows[0].registry_source} — the partner's own published disclosures for
                    their own reporting year, unaudited by us, never blended with mGrant grant
                    figures and secondary to the mGrant diligence above.
                  </CaveatNote>
                </>
              ) : (
                <p className="text-[13px] text-stone-600 leading-relaxed">
                  {detail.rows[0].registry_match_note}
                </p>
              )}
            </EvidenceSection>

            <div className="rule my-7" />
            <div className="text-[12px] font-medium text-stone-600 mb-3">
              Utilisation by financial year
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              {Object.entries(detail.rows[0].utilisation_by_fy).map(([k, v]) => (
                <div key={k}>
                  <span className="num text-[13px] text-stone-600">{k}</span>{" "}
                  <span className="num text-[14px] text-stone-900">{pct(v)}</span>
                </div>
              ))}
            </div>

            <div className="rule my-7" />
            <div className="text-[12px] font-medium text-stone-600 mb-3">
              Grants ({detail.grants.length})
            </div>
            <DataTable
              testId="partner-grants"
              rows={detail.grants}
              columns={[
                { key: "grant_id", label: "Grant", render: (r) => (
                  <div>
                    <div className="num text-[12px] text-stone-600">{r.grant_id} · {r.fy}</div>
                    <div className="text-[13px] text-stone-900 mt-0.5">{r.grant_name}</div>
                    <div className="text-[12px] text-stone-600 mt-0.5">
                      {r.districts.join(", ") || `geo: ${r.geo_match_level}`}
                    </div>
                  </div>
                )},
                { key: "funds_received", label: "Received", align: "right",
                  className: "whitespace-nowrap", render: (r) => inr(r.funds_received) },
                { key: "rate", label: "Util.", align: "right", className: "whitespace-nowrap",
                  render: (r) => pct(r.funds_received ? (100 * r.funds_utilised) / r.funds_received : null) },
              ]}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
}

export const Select = ({ value, onChange, options, label, testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="bg-white border border-stone-200 rounded-full px-4 py-2 text-[13px] text-stone-700 outline-none focus-visible:ring-2 focus-visible:ring-[#A01236] hover:border-stone-300 transition-colors"
  >
    {label !== undefined && <option value="">{label}</option>}
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);
